// F3 客户端身份密钥 —— Ed25519 keypair, 存 IndexedDB, 永不离开本机
//
// 设计要点（docs/F-series-approval-design.html §6）:
//   - 每台设备一份 keypair；user 在 server 端可有多把 pubkey
//   - seed (32 byte) 写 IndexedDB 表 'identity_key'，key="seed"
//   - 私钥永远不发到 server / agent / 任何外部 endpoint
//   - 只上传 publicKey base64 + device_label 给 server
//
// 注意：F1 范围里假定 IndexedDB 可信。Web 端依赖浏览器同源策略；
// Tauri 端后续 F5+ 可换 OS keychain（macOS Keychain / Win DPAPI）。

import { Ed25519Signer } from "@chatbox/protocol";

const DB_NAME = "chatbox-identity";
const STORE = "identity_key";
const KEY = "seed";
const DB_VERSION = 1;

let _cached: { signer: Ed25519Signer; publicKey: Uint8Array } | null = null;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function readSeed(): Promise<Uint8Array | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const store = tx.objectStore(STORE);
    const req = store.get(KEY);
    req.onsuccess = () => {
      const v = req.result;
      if (v instanceof Uint8Array) resolve(v);
      else resolve(null);
    };
    req.onerror = () => reject(req.error);
  });
}

async function writeSeed(seed: Uint8Array): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    const req = store.put(seed, KEY);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

function randomSeed(): Uint8Array {
  const seed = new Uint8Array(32);
  crypto.getRandomValues(seed);
  return seed;
}

/**
 * 加载或首次生成本地 keypair。
 * 调用此函数后，签名/公钥都从内存 cache 取。
 */
export async function ensureIdentityKey(): Promise<{
  signer: Ed25519Signer;
  publicKey: Uint8Array;
}> {
  if (_cached) return _cached;
  let seed = await readSeed();
  if (!seed) {
    seed = randomSeed();
    await writeSeed(seed);
  }
  const signer = Ed25519Signer.fromSeed(seed);
  const publicKey = signer.publicKey();
  _cached = { signer, publicKey };
  return _cached;
}

/** 测试 / 重置用 —— 删本机 seed，下次启动会重生成新 keypair。 */
export async function resetIdentityKey(): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const req = tx.objectStore(STORE).delete(KEY);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
  _cached = null;
}

/** base64 编码（浏览器 btoa, 输入是 binary bytes）。 */
export function bytesToBase64(b: Uint8Array): string {
  let s = "";
  for (let i = 0; i < b.length; i++) s += String.fromCharCode(b[i]);
  return btoa(s);
}

/** base64 解码 → Uint8Array。 */
export function base64ToBytes(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** 默认 device label = navigator.platform + 短随机 ID（避免冲撞）。 */
export function defaultDeviceLabel(): string {
  const plat = typeof navigator !== "undefined" ? navigator.platform : "Web";
  const rand = Math.random().toString(36).slice(2, 6);
  return `${plat} · ${rand}`;
}
